import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { GridColDef, GridMoreVertIcon, GridRowParams } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useState } from "react";
import AddIcon from "../../src/assets/AddIcon.svg";
import { getRIOGrid, removeRIOFromUser } from "../api/requests/RIO";
import CommDataGrid from "../components/CommDataGrid";
import DeleteDialog from "../components/common/DeleteDialog";
import CreateOrEditRIO from "../components/CreateOrEditRIO";
import { useAlert } from "../context/AlertContext";
import { useAuth } from "../context/AuthContext";
import { commonStrings } from "../static/commonStrings";
import { RIOType } from "../utils/types";
import { menuItemStyles, PaginationType } from "./Groups";
import { containedButtonWithIconSx } from "./Users";
import Zone from "./Zone";

const RIO: React.FC = () => {
  const [rioList, setRioList] = useState<RIOType[]>([]);
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const [selectedRow, setSelectedRow] = useState<RIOType | null>(null);
  const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
  const [totalRowsCount, setTotalRowsCount] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [isEditDialog, setIsEditDialog] = useState<boolean>(false);
  const [selectedRIO, setSelectedRIO] = useState<RIOType | null>(null);
  const [paginationModel, setPaginationModel] = useState<PaginationType>({
    page: 0,
    pageSize: 10,
  });
  const getAllRIOList = async () => {
    const rioResponse = await getRIOGrid({
      page: paginationModel.page,
      size: paginationModel.pageSize,
      searchQuery: searchText.trim(),
    });
    if (rioResponse.status == 200) {
      const formatted = rioResponse.data.rio.map((item: RIOType) => ({
        id: item.rioId,
        ...item,
      }));

      setRioList(formatted);
      setTotalRowsCount(rioResponse.data.totalCount || 0);
    }

    setLoading(false);
  };

  const fetchRIOData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([getAllRIOList()]);
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, searchText]);

  useEffect(() => {
    setLoading(true);
    fetchRIOData();
  }, [fetchRIOData]);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSearch = useCallback((value: string) => {
    setSearchText(value.trim());
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleEdit = () => {
    if (selectedRow) {
      handleMenuClose();
    }
    setOpenCreateDialog(true);
    setIsEditDialog(true);
  };
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    row: RIOType
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleDelete = async () => {
    if (!selectedRow) {
      showAlert("No RIO selected", "error");
      return;
    }
    try {
      const removeRIORes = await removeRIOFromUser(selectedRow.rioId);
      if (removeRIORes.status == 200) {
        showAlert("Deleted successfully", "success");
        setSelectedRow(null);
        handleMenuClose();
        closeCreateDialog();
        getAllRIOList();
      }
    } catch (error: any) {
      showAlert("errorMessage", "error");
      setSelectedRow(null);
    }
    handleMenuClose();
  };

  const createRIO = () => {
    setOpenCreateDialog(true);
  };

  const buttonsComponent = useCallback(
    () => (
      <>
        {user?.permissions && (
          <>
            {user.permissions.some(
              (p) => p.module === "Users" && p.actions.includes("Create")
            ) ? (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={createRIO}
                  sx={containedButtonWithIconSx}
                >
                  <img src={AddIcon} /> Add RIO
                </Button>
              </Box>
            ) : null}
          </>
        )}
      </>
    ),
    [user]
  );

  const handleSaveRIO = async () => {
    closeCreateDialog();
    getAllRIOList();
  };

  const closeCreateDialog = () => {
    setOpenCreateDialog(false);
    setOpenDeleteDialog(false);
    setIsEditDialog(false);
  };

  const filteredList = rioList.filter((rio) =>
    rio.rioName.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleOpenZoneTable = (row: GridRowParams<RIOType>) => {
    setSelectedRIO(row.row);
  };

  const columns: GridColDef[] = [
    {
      field: "rio",
      headerName: "RIO",
      flex: 1,
      headerClassName: "custom-header",
      disableColumnMenu: true,
      renderCell: ({ row }) => {
        return (
          <Typography key={row.id} variant="body2" sx={{ display: "contents" }}>
            {row.rioName || "--"}
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      sortable: false,
      disableColumnMenu: true,
      headerClassName: "custom-header",
      renderCell: (params) => (
        <>
          {params.row.status !== "Deleted" && (
            <>
              <IconButton
                onClick={(event) => {
                  handleMenuOpen(event, params.row);
                }}
              >
                <GridMoreVertIcon sx={{ color: "#64748B" }} />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                sx={menuItemStyles}
              >
                {user?.permissions && (
                  <>
                    {user.permissions.some(
                      (p) => p.module === "Users" && p.actions.includes("Edit")
                    ) ? (
                      <MenuItem onClick={handleEdit}>Edit</MenuItem>
                    ) : null}
                  </>
                )}{" "}
                {user?.permissions && (
                  <>
                    {user.permissions.some(
                      (p) =>
                        p.module === "Users" && p.actions.includes("Delete")
                    ) ? (
                      <MenuItem
                        onClick={() => selectedRow && setOpenDeleteDialog(true)}
                      >
                        Delete
                      </MenuItem>
                    ) : null}
                  </>
                )}{" "}
              </Menu>
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
      <Box sx={{ flex: 1, maxWidth: "100%" }}>
        <CommDataGrid
          title="RIO"
          description="View and manage all RIO in the system"
          rows={filteredList}
          columns={columns}
          section="RIO"
          renderButtons={buttonsComponent}
          setPaginationModel={setPaginationModel}
          paginationModel={paginationModel}
          totalRowsCount={totalRowsCount}
          searchText={searchText}
          handleSearch={handleSearch}
          loading={loading}
          onRowClick={handleOpenZoneTable}
        />
        {openCreateDialog ? (
          <CreateOrEditRIO
            onSaveRIOCB={handleSaveRIO}
            setOpenCreateDialog={closeCreateDialog}
            isEdit={isEditDialog}
            rioData={selectedRow!}
          />
        ) : null}
        {openDeleteDialog ? (
          <DeleteDialog
            actionButton={handleDelete}
            cancelButton={() => setOpenDeleteDialog(!openDeleteDialog)}
            dialogSubTitle={`RIO ${selectedRow?.rioName} will be deleted, if you click on 'Yes'`}
            dialogTextTitle={`Are you sure want to delete ${selectedRow?.rioName}?`}
            title={`Delete ${commonStrings.sideBarMenuTitles.rio}`}
          />
        ) : null}
      </Box>
      {selectedRIO && (
        <Box sx={{ flex: 1, maxWidth: "100%" }}>
          <Zone selectedRIO={selectedRIO} />
        </Box>
      )}
    </Box>
  );
};

export default RIO;
